import type { ConfigMap } from 'Models/uiConfig/requestTypes';

type Key<K extends string = string> = K | Key<K>[];

function keysSelector<O, R extends Partial<O>>(...args: Key<Extract<keyof O, string>>[]): (obj: O) => R;
// eslint-disable-next-line no-redeclare
function keysSelector(...args: string[]): (obj: object) => ConfigMap;
// eslint-disable-next-line no-redeclare
function keysSelector(...args: Key[]): (obj: unknown) => ConfigMap {
  const paths = keysToPaths(args);
  return paths.length ? (obj) => pickPaths(obj, paths) as ConfigMap : () => ({});
}

export default keysSelector;

function keysToPaths(keys: Key[], prefix = ''): string[] {
  return keys.reduce<string[]>((result, key) => {
    if (Array.isArray(key)) {
      const [nextKey, ...nextKeys] = key;
      return [...result, ...keysToPaths(nextKeys, `${prefix}${nextKey}.`)];
    }
    return [...result, `${prefix}${key}`];
  }, []);
}

function pickPaths(source: unknown, paths: string[]): object {
  if (!isNonNullObject(source)) {
    return {};
  }

  const result: Record<string, unknown> = {};
  paths.forEach((path) => {
    const segments = path.split('.');
    const value = getDefinedPathValue(source, segments);
    if (value !== undefined) {
      setPathValue(result, segments, value);
    }
  });

  return result;
}

function getDefinedPathValue(source: unknown, segments: string[]): unknown {
  let current: unknown = source;

  for (const segment of segments) {
    if (!isNonNullObject(current) || !(segment in current)) {
      return undefined;
    }
    current = current[segment];
  }

  return current;
}

function setPathValue(target: Record<string, unknown>, segments: string[], value: unknown): void {
  let current = target;

  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      current[segment] = value;
      return;
    }

    const existing = current[segment];
    if (isNonNullObject(existing)) {
      current = existing;
    } else {
      const next: Record<string, unknown> = {};
      current[segment] = next;
      current = next;
    }
  });
}

function isNonNullObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
